import { api } from "../api";
import { getAnalyticsDateRange } from "./getAnalyticsDateRange";

const retryWithBackoff = async (fn, valueExtractor) => {
  const maxRetries = 3;
  const delay = 300;
  for (let i = 0; i < maxRetries; i++) {
    const result = await fn();
    const value = valueExtractor(result);
    if (value !== null && value !== undefined) {
      return result;
    }
    if (i < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
    }
  }
  return await fn();
};

/**
 * Fetch all analytics stats from Gadget views with streaming updates.
 * Each metric is displayed as soon as it's available.
 *
 * @param {Object} params
 * @param {string} params.customerId - The customer ID
 * @param {string} params.shopId - The shop ID (for turnover/sales)
 * @param {string} params.selectedDateFilter - The selected date filter (Today, ThisWeek, etc.)
 * @param {Date} params.startDate - Custom start date for "Between" filter
 * @param {Date} params.endDate - Custom end date for "Between" filter
 * @param {Function} params.onUpdate - Callback called with partial stats as they arrive
 * @returns {Promise<void>} Resolves when all metrics are fetched
 */
export const fetchAnalyticsStats = async ({
  customerId,
  shopId,
  selectedDateFilter,
  startDate,
  endDate,
  onUpdate,
}) => {
  const { from, to } = getAnalyticsDateRange(
    selectedDateFilter,
    startDate,
    endDate,
  );

  const commonParams = { customerId, from, to };
  const shopParams = { shopId, from, to };
  const suffix = selectedDateFilter;

  const metricsFetchers = [
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsSent(commonParams),
          (r) => r?.messages?.sent,
        ),
      transform: (result) => ({
        [`sent${suffix}`]: result?.messages?.sent ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsDelivered(commonParams),
          (r) => r?.messages?.delivered,
        ),
      transform: (result) => ({
        [`delivered${suffix}`]: result?.messages?.delivered ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsRead(commonParams),
          (r) => r?.messages?.read,
        ),
      transform: (result) => ({
        [`read${suffix}`]: result?.messages?.read ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsOptins(commonParams),
          (r) => r?.contacts?.optins,
        ),
      transform: (result) => ({
        [`optins${suffix}`]: result?.contacts?.optins ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsOptouts(commonParams),
          (r) => r?.contacts?.optoutCount,
        ),
      transform: (result) => ({
        [`optoutCount${suffix}`]: result?.contacts?.optoutCount ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsMessagesCount(commonParams),
          (r) => r?.messages?.messagesCount,
        ),
      transform: (result) => ({
        [`messagesCount${suffix}`]: result?.messages?.messagesCount ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsWhatsappCost(commonParams),
          (r) => r?.messages?.totalWhatsappCostInEuros,
        ),
      transform: (result) => ({
        [`totalWhatsappCostInEuros${suffix}`]:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
        totalWhatsappCostInEuros:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsExternalTurnover(commonParams),
          (r) => r?.messages?.externalTurnover,
        ),
      transform: (result) => ({
        [`externalTurnover${suffix}`]: result?.messages?.externalTurnover ?? 0,
      }),
    },
  ];

  if (shopId) {
    metricsFetchers.push(
      {
        fetch: () =>
          retryWithBackoff(
            () => api.getAnalyticsTurnover(shopParams),
            (r) => r?.shopifyOrders?.turnover,
          ),
        transform: (result) => ({
          [`turnover${suffix}`]: result?.shopifyOrders?.turnover ?? 0,
        }),
      },
      {
        fetch: () =>
          retryWithBackoff(
            () => api.getAnalyticsSales(shopParams),
            (r) => r?.shopifyOrders?.sales,
          ),
        transform: (result) => ({
          [`sales${suffix}`]: result?.shopifyOrders?.sales ?? 0,
        }),
      },
    );
  }

  // Launch all fetches in parallel, update state as each completes
  const promises = metricsFetchers.map(async ({ fetch, transform }) => {
    try {
      const result = await fetch();
      const partialStats = transform(result);
      onUpdate((prev) => ({ ...prev, ...partialStats }));
    } catch (error) {
      console.error("Error fetching metric:", error);
    }
  });

  await Promise.all(promises);
};
